import { useTranslation } from "@equiem/web-ng-lib";
import { Button, notNullOrUndefined, ProgressCircle, Site, useShowError, Viewer } from "@equiem/web-ng-lib";
import React, { MutableRefObject, useCallback, useContext, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { CommentsForContentQuery, useCommentsForContentQuery } from "../../generated/gateway-client";
import { ContentType } from "../../lib/ContentType";
import { CommentDots } from "../icons/CommentDots";
import { Comment, Props as CommentProps } from "./Comment";
import { CommentForm } from "./CommentForm";

interface Props {
  contentUuid: string;
  contentType: ContentType;
  inputRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
  selectComment?: string;
}

type Comment = CommentProps["comment"];

export const CommentsForContent: React.FC<Props> = ({ contentUuid, contentType, inputRef: commentRef, selectComment }) => {
  const { t } = useTranslation();
  const site = useContext(Site);
  const viewer = useContext(Viewer);
  const showError = useShowError();
  const pageSize = 10;
  const pauseEveryNPages = 3;
  const [refreshing, setRefreshing] = useState(false)
  const { data, loading, fetchMore, updateQuery, variables, refetch } = useCommentsForContentQuery({
    variables: { contentUuid, siteUuid: site.uuid, first: pageSize },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });
  const comments = data?.commentsForContent?.edges
  .map((e) => e?.node)
  .filter(notNullOrUndefined) ?? [];

  useEffect(() => {
    if (selectComment != null) {
      refetch(variables).catch(showError);
    }
  }, [selectComment]);

  const pageNum = Math.ceil(comments.length / pageSize);
  const hasMore = data?.commentsForContent?.pageInfo.hasNextPage ?? false;
  const pauseInfiniteScroll = pageNum % pauseEveryNPages === 0;

  const next = useCallback(() => fetchMore({
    variables: {
      after: data?.commentsForContent?.pageInfo.endCursor,
    },
  }), [data?.commentsForContent?.pageInfo.endCursor]);

  const replaceEdges = (newEdges: CommentsForContentQuery["commentsForContent"]["edges"]) => {
    updateQuery((prev) => ({
      ...prev,
      commentsForContent: {
        ...prev.commentsForContent,
        edges: newEdges,
      },
    }));
  };

  const onAddComment = async ({ comment }: { comment: Comment}) => {
    setRefreshing(true);
    replaceEdges([
      { node: { ...comment, comments: [] } },
      ...(data?.commentsForContent?.edges ?? []),
    ]);
    await refetch(variables);
    setRefreshing(false);
  };

  const onDeleteComment = async ({ comment }: { comment: Comment}) => {
    replaceEdges(
      (data?.commentsForContent?.edges ?? [])
        .filter(notNullOrUndefined)
        .filter(({ node }) => node != null && node.uuid !== comment.uuid)
        .map((edge) => ({
          ...edge, node: {
            ...edge.node!,
            comments: edge.node!.comments.filter(
              (c) => c.uuid !== comment.uuid,
            ),
          },
        })),
    );
  };

  return (
    <div className="root">
      { viewer?.permissions.writeComments === true ? (
        <CommentForm
          contentUuid={contentUuid}
          contentType={contentType}
          onAddComment={onAddComment}
          bodyRef={commentRef}
          className="mb-3"
        />
      ) : null}
      { viewer?.permissions.readComments === true ? (
        <InfiniteScroll
          next={next}
          dataLength={comments.length}
          hasMore={hasMore && !pauseInfiniteScroll}
          loader={<span key={0}></span>}
          style={{ marginTop: "-15px" }}
        >
          {comments.length > 0 ? comments.map((comment) => (
            <Comment
              contentType={contentType}
              contentUuid={contentUuid}
              onDelete={onDeleteComment}
              key={comment.uuid}
              comment={comment}
            />
          )) : ( loading ? null : (
            <div className="none text-center text-muted my-5">
              <p style={{ fontSize: "4rem", opacity: 0.3 }}><CommentDots size={66} /></p>
              <p>{t("main.noCommentsAddYoursAbove")}</p>
            </div>
          ))}
          { refreshing ? null : (
            <div className="text-center py-3">{
              loading
                ? <ProgressCircle size="lg" />
                : hasMore ?
                  <Button
                    variant="outline"
                    onClick={next}
                  >{t("main.loadMore")}</Button>
                  : null
            }</div>
          )}
        </InfiniteScroll>
      ) : null}
      <style jsx>{`
      .root :global(.infinite-scroll-component) {
        overflow: visible !important;
      }
      `}</style>
    </div>
  );
};
